'use client';

import React from 'react';

import {
  LayoutProvider as ContextLayoutProvider,
  LayoutProviderProps as ContextComponentLayoutProviderProps,
  useLayout,
} from '../contexts/layout-context';

import { Shell, ShellProps } from './shell';
import { TopBar, TopBarProps } from './topbar';

export interface ComponentComponentLayoutProviderProps
  extends Omit<ContextComponentLayoutProviderProps, 'children'> {
  children: React.ReactNode;
  /**
   * Shell component props
   */
  shellProps?: Partial<ShellProps>;
  /**
   * TopBar component props
   */
  topBarProps?: Partial<TopBarProps>;
  /**
   * Whether to render the layout components (Shell and TopBar)
   */
  renderLayout?: boolean;
  /**
   * Custom header component to replace TopBar
   */
  customHeader?: React.ReactNode;
  /**
   * Whether to show the topbar
   */
  showTopBar?: boolean;
  /**
   * Whether to show the sidebar
   */
  showSidebar?: boolean;
}

interface LayoutContentProps {
  children: React.ReactNode;
  shellProps?: Partial<ShellProps>;
  topBarProps?: Partial<TopBarProps>;
  renderLayout?: boolean;
  customHeader?: React.ReactNode;
  showTopBar?: boolean;
  showSidebar?: boolean;
}

// Internal component that uses the layout context
function LayoutContent({
  children,
  shellProps,
  topBarProps,
  renderLayout = true,
  customHeader,
  showTopBar = true,
  showSidebar: _showSidebar = true,
}: LayoutContentProps) {
  const {
    isSidebarCollapsed,
    toggleSidebar,
    isMobileMenuOpen,
    toggleMobileMenu,
    setMobileMenuOpen: _setMobileMenuOpen,
  } = useLayout();

  if (!renderLayout) {
    return <>{children}</>;
  }

  // Prepare the header content
  const headerContent = showTopBar
    ? customHeader || <TopBar onMobileMenuToggle={toggleMobileMenu} {...topBarProps} />
    : null;

  return (
    <Shell
      header={headerContent}
      sidebarProps={{
        collapsed: isSidebarCollapsed,
        onCollapse: toggleSidebar,
        open: isMobileMenuOpen,
        onToggle: toggleMobileMenu,
        ...shellProps?.sidebarProps,
      }}
      {...shellProps}
    >
      {children}
    </Shell>
  );
}

/**
 * Layout Provider that combines context and UI components
 * Provides a complete layout solution with sidebar, topbar, and content area
 */
export function LayoutShell({
  children,
  defaultSidebarCollapsed,
  defaultPreferences,
  onPreferencesChange,
  ...layoutProps
}: ComponentComponentLayoutProviderProps) {
  return (
    <ContextLayoutProvider
      defaultSidebarCollapsed={defaultSidebarCollapsed}
      defaultPreferences={defaultPreferences}
      onPreferencesChange={onPreferencesChange}
    >
      <LayoutContent {...layoutProps}>{children}</LayoutContent>
    </ContextLayoutProvider>
  );
}

// Export with display name for debugging
LayoutShell.displayName = 'LayoutShell';

// Re-export types with different name to avoid conflicts
export type LayoutShellProps = ComponentComponentLayoutProviderProps;
